import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Field,
  Input,
  Modal,
  useToast,
} from '@ambernord/ui';
import { AlertTriangle, Copy, KeyRound, ShieldCheck } from 'lucide-react';
import { toDataURL } from 'qrcode';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext.js';
import { AppLayout } from '../components/AppLayout.js';
import {
  type ApiResult,
  type MfaEnableResponse,
  mfaDisable,
  mfaEnable,
  mfaRegenerateRecoveryCodes,
  mfaSetup,
} from '../lib/api.js';
import { playAudioCue } from '../lib/audio.js';

/**
 * Konta drošība: TOTP divfaktoru autentifikācija (§2 S2) + rezerves kodi.
 * `mfa/setup` → QR kods + atslēga → `mfa/enable` ar kodu → 10 rezerves kodi (rāda VIENU reizi).
 * Ieslēgtā stāvoklī: atlikušo kodu skaits (brīdinājums pie 0), "Ģenerēt jaunus kodus" (prasa
 * TOTP) un "Izslēgt" (prasa TOTP vai rezerves kodu).
 */

interface PendingSetup {
  secret: string;
  uri: string;
  qrDataUrl: string;
}

type Dialog = 'regenerate' | 'disable';

const TOTP_LENGTH = 6;

function isInvalidCodeError(error: string | undefined): boolean {
  return error === 'invalid_code' || error === 'invalid_input';
}

function RecoveryCodesOnce({
  codes,
  onDismiss,
}: {
  codes: string[];
  onDismiss: () => void;
}) {
  const { t } = useTranslation('security');
  const { toast } = useToast();

  async function copyCodes() {
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      toast(t('mfa.recovery.copied'));
    } catch {
      toast(t('mfa.errors.generic'), 'error');
    }
  }

  return (
    <div
      className="rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-700 dark:bg-amber-950/40"
      data-testid="mfa-recovery-codes"
    >
      <div className="flex items-start gap-3">
        <KeyRound
          className="mt-0.5 h-5 w-5 shrink-0 text-amber-700 dark:text-amber-400"
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-slate-900 dark:text-white">
            {t('mfa.recovery.heading')}
          </p>
          <p className="mt-1 text-sm text-amber-900 dark:text-amber-200" role="alert">
            {t('mfa.recovery.warning')}
          </p>
          <ul className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1 font-mono text-sm tracking-wider text-slate-900 sm:grid-cols-3 dark:text-slate-100">
            {codes.map((code) => (
              <li key={code} className="select-all" data-testid="mfa-recovery-code">
                {code}
              </li>
            ))}
          </ul>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => void copyCodes()}
              data-testid="mfa-recovery-copy"
            >
              <Copy className="h-4 w-4" aria-hidden />
              {t('mfa.recovery.copy')}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onDismiss}
              data-testid="mfa-recovery-dismiss"
            >
              {t('mfa.recovery.saved')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Security() {
  const { t } = useTranslation('security');
  const { user, refresh } = useAuth();
  const { toast } = useToast();

  const [setup, setSetup] = useState<PendingSetup | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** Svaigi rezerves kodi — rāda vienu reizi pēc ieslēgšanas vai atjaunošanas. */
  const [freshCodes, setFreshCodes] = useState<string[] | null>(null);

  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [dialogCode, setDialogCode] = useState('');
  const [dialogError, setDialogError] = useState<string | null>(null);

  const enabled = user?.mfaEnabled ?? false;
  const codesLeft = user?.mfaRecoveryCodesLeft ?? null;

  async function startSetup() {
    setBusy(true);
    setError(null);
    try {
      const res = await mfaSetup();
      if (!res.ok || !res.data.secret || !res.data.uri) {
        toast(t('mfa.errors.generic'), 'error');
        return;
      }
      const qrDataUrl = await toDataURL(res.data.uri, { width: 208, margin: 1 });
      setSetup({ secret: res.data.secret, uri: res.data.uri, qrDataUrl });
      setCode('');
    } catch {
      toast(t('mfa.errors.generic'), 'error');
    } finally {
      setBusy(false);
    }
  }

  async function confirm(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await mfaEnable(code);
      if (!res.ok || !res.data.ok) {
        setError(
          t(isInvalidCodeError(res.data.error) ? 'mfa.errors.invalidCode' : 'mfa.errors.generic'),
        );
        playAudioCue('error');
        return;
      }
      playAudioCue('success');
      toast(t('mfa.success'));
      setSetup(null);
      setCode('');
      setFreshCodes(res.data.recoveryCodes ?? null);
      await refresh();
    } catch {
      setError(t('mfa.errors.generic'));
    } finally {
      setBusy(false);
    }
  }

  async function copySecret() {
    if (!setup) return;
    try {
      await navigator.clipboard.writeText(setup.secret);
      toast(t('mfa.copied'));
    } catch {
      toast(t('mfa.errors.generic'), 'error');
    }
  }

  function openDialog(kind: Dialog) {
    setDialog(kind);
    setDialogCode('');
    setDialogError(null);
  }

  function closeDialog() {
    if (busy) return;
    setDialog(null);
  }

  async function submitDialog(e: FormEvent) {
    e.preventDefault();
    if (!dialog) return;
    setBusy(true);
    setDialogError(null);
    try {
      const res: ApiResult<MfaEnableResponse> =
        dialog === 'regenerate'
          ? await mfaRegenerateRecoveryCodes(dialogCode)
          : await mfaDisable(dialogCode);
      if (!res.ok || !res.data.ok) {
        setDialogError(
          t(isInvalidCodeError(res.data.error) ? 'mfa.errors.invalidCode' : 'mfa.errors.generic'),
        );
        playAudioCue('error');
        return;
      }
      playAudioCue('success');
      if (dialog === 'regenerate') {
        toast(t('mfa.recovery.regenerated'));
        setFreshCodes(res.data.recoveryCodes ?? null);
      } else {
        toast(t('mfa.disable.success'));
        setFreshCodes(null);
      }
      setDialog(null);
      await refresh();
    } catch {
      setDialogError(t('mfa.errors.generic'));
    } finally {
      setBusy(false);
    }
  }

  const dialogIsTotp = dialog === 'regenerate';
  const dialogReady = dialogIsTotp
    ? dialogCode.length === TOTP_LENGTH
    : dialogCode.trim().length >= 6;

  return (
    <AppLayout title={t('title')}>
      <Card className="max-w-2xl">
        <CardHeader
          title={t('mfa.heading')}
          subtitle={t('mfa.description')}
          action={
            <Badge tone={enabled ? 'green' : 'slate'} data-testid="mfa-status">
              {enabled ? t('mfa.statusOn') : t('mfa.statusOff')}
            </Badge>
          }
        />
        <CardBody>
          {enabled ? (
            <div className="flex flex-col gap-5">
              <div className="flex items-start gap-3" data-testid="mfa-enabled">
                <ShieldCheck
                  className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400"
                  aria-hidden
                />
                <div>
                  <p className="text-sm text-slate-700 dark:text-slate-300">
                    {t('mfa.enabledText')}
                  </p>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    {t('mfa.enabledHint')}
                  </p>
                </div>
              </div>

              {freshCodes ? (
                <RecoveryCodesOnce codes={freshCodes} onDismiss={() => setFreshCodes(null)} />
              ) : codesLeft === 0 ? (
                <div
                  className="flex items-start gap-3 rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
                  role="alert"
                  data-testid="mfa-recovery-left"
                >
                  <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
                  <span>{t('mfa.recovery.none')}</span>
                </div>
              ) : (
                <p
                  className="text-sm text-slate-600 dark:text-slate-400"
                  data-testid="mfa-recovery-left"
                >
                  {t('mfa.recovery.left', { count: codesLeft ?? 0 })}
                </p>
              )}

              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => openDialog('regenerate')}
                  disabled={busy}
                  data-testid="mfa-regenerate"
                >
                  {t('mfa.recovery.regenerate')}
                </Button>
                <Button
                  type="button"
                  variant="danger"
                  onClick={() => openDialog('disable')}
                  disabled={busy}
                  data-testid="mfa-disable"
                >
                  {t('mfa.disable.action')}
                </Button>
              </div>
            </div>
          ) : setup ? (
            <form className="flex flex-col gap-5" onSubmit={(e) => void confirm(e)} noValidate>
              <div>
                <p className="mb-2 text-sm font-medium text-slate-900 dark:text-white">
                  {t('mfa.step1')}
                </p>
                <img
                  src={setup.qrDataUrl}
                  alt={t('mfa.qrAlt')}
                  width={208}
                  height={208}
                  className="rounded-lg border border-slate-200 bg-white p-1 dark:border-slate-700"
                />
                <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
                  {t('mfa.cantScan')}
                </p>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <code
                    data-testid="mfa-secret"
                    className="select-all break-all rounded-md bg-slate-100 px-2 py-1 font-mono text-sm tracking-wider text-slate-900 dark:bg-slate-800 dark:text-slate-100"
                  >
                    {setup.secret}
                  </code>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => void copySecret()}
                    aria-label={t('mfa.copy')}
                  >
                    <Copy className="h-4 w-4" aria-hidden />
                    {t('mfa.copy')}
                  </Button>
                </div>
              </div>

              <div>
                <p className="mb-2 text-sm font-medium text-slate-900 dark:text-white">
                  {t('mfa.step2')}
                </p>
                <Field label={t('mfa.code')} htmlFor="mfa-code" error={error} className="max-w-xs">
                  <Input
                    id="mfa-code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9]*"
                    maxLength={TOTP_LENGTH}
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                    className="text-center text-lg tracking-widest"
                  />
                </Field>
              </div>

              <div className="flex gap-2">
                <Button
                  type="submit"
                  disabled={busy || code.length !== TOTP_LENGTH}
                  data-testid="mfa-confirm"
                >
                  {busy ? t('common:status.loading') : t('mfa.confirm')}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  disabled={busy}
                  onClick={() => {
                    setSetup(null);
                    setCode('');
                    setError(null);
                  }}
                >
                  {t('mfa.cancel')}
                </Button>
              </div>
            </form>
          ) : (
            <Button
              type="button"
              onClick={() => void startSetup()}
              disabled={busy}
              data-testid="mfa-enable"
            >
              {busy ? t('common:status.loading') : t('mfa.enable')}
            </Button>
          )}
        </CardBody>
      </Card>

      <Modal
        open={dialog !== null}
        onClose={closeDialog}
        title={dialogIsTotp ? t('mfa.recovery.regenerateTitle') : t('mfa.disable.title')}
      >
        <form className="flex flex-col gap-4" onSubmit={(e) => void submitDialog(e)} noValidate>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            {dialogIsTotp ? t('mfa.recovery.regenerateHint') : t('mfa.disable.hint')}
          </p>
          <Field
            label={dialogIsTotp ? t('mfa.code') : t('mfa.disable.code')}
            htmlFor="mfa-dialog-code"
            error={dialogError}
            className="max-w-xs"
          >
            <Input
              id="mfa-dialog-code"
              inputMode={dialogIsTotp ? 'numeric' : 'text'}
              autoComplete="one-time-code"
              autoCapitalize="characters"
              maxLength={dialogIsTotp ? TOTP_LENGTH : 16}
              required
              value={dialogCode}
              onChange={(e) =>
                setDialogCode(dialogIsTotp ? e.target.value.replace(/\D/g, '') : e.target.value)
              }
              className="text-center text-lg tracking-widest"
            />
          </Field>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="ghost" onClick={closeDialog} disabled={busy}>
              {t('mfa.cancel')}
            </Button>
            <Button
              type="submit"
              variant={dialogIsTotp ? 'primary' : 'danger'}
              disabled={busy || !dialogReady}
              data-testid="mfa-dialog-submit"
            >
              {busy
                ? t('common:status.loading')
                : dialogIsTotp
                  ? t('mfa.recovery.regenerateConfirm')
                  : t('mfa.disable.confirm')}
            </Button>
          </div>
        </form>
      </Modal>
    </AppLayout>
  );
}
